// RespondSurveyPage.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, Typography, Card, CardContent } from "@mui/material";
import SurveyPreview from "../../components/Survey/SurveyPreview";
import api from "../../api";
import { parseJWTToken } from "../../helpers";
import RespondentIntlProvider from "../../components/RespondentIntlProvider";
import VoiceSurveyRunner from "./voice/VoiceSurveyRunner";

function adaptSurveyToSurveyPreviewSteps(survey) {
  // Convert /questions API shape to SurveyPreview's 'steps' shape
  // SurveyPreview expects uppercase types and 'question' instead of 'text'
  return (survey?.questions || []).map((q) => {
    const t = (q.type || "").toUpperCase(); // e.g. "single_choice" -> "SINGLE_CHOICE"
    const normalizedType =
      t === "SINGLE_CHOICE" ? "SINGLE_CHOICE" :
        t === "MULTI_CHOICE" ? "MULTI_CHOICE" :
          t === "OPEN_TEXT" ? "OPEN_TEXT" :
            t === "LIKERT_SCALE" ? "LIKERT_SCALE" :
              t === "PAGE_BREAK" ? "PAGE_BREAK" :
                "TEXT"; // fallback

    let choices = [];
    if (normalizedType === "LIKERT_SCALE") {
      choices = q.scale?.map((s) => ({
        id: s.id,
        text: s.text,
        position: s.position,
      }));
    } else {
      choices = q.choices?.map((c) => ({
        id: c.id,
        text: c.text,
        position: c.position,
      }));
    }
    return {
      id: q.id,                        // keep original id for answers
      type: normalizedType,
      question: q.text || "",          // PreviewTab uses 'question' HTML
      description: q.description || "",
      is_required: !!q.is_required,
      settings: q.settings || {},
      choices: choices,
      // add any additional fields you need
    };
  });
}

// The page proper — wrapped below so the invite's ?lang= provider sits above it.
function RespondSurveyView() {
  const { surveyId, token } = useParams();
  const [loading, setLoading] = useState(true);
  const [survey, setSurvey] = useState(null);
  const [error, setError] = useState("");
  const [hasTrackedEmailOpen, setHasTrackedEmailOpen] = useState(false);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        setError("");
        const res = await api.getRespondSurvey(surveyId, token);
        if (alive) setSurvey(res);
      } catch (e) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [surveyId, token]);

  // Track survey opening for EMAIL collectors - runs after render
  useEffect(() => {
    if (!token || hasTrackedEmailOpen) return;

    const trackEmailSurveyOpen = async () => {
      try {
        const tokenData = parseJWTToken(token);

        if (tokenData && tokenData.collector_id) {
          if (tokenData.type === undefined) {
            console.log('Tracking EMAIL survey open for collector:', tokenData.collector_id);
            const result = await api.trackSurveyOpen(tokenData.collector_id, token);
            console.log('EMAIL tracking result:', result);
            setHasTrackedEmailOpen(true); // Mark as tracked to prevent duplicates
          } else {
            console.log('Not an EMAIL collector, type:', tokenData.type);
          }
        } else {
          console.log('Token data missing required fields:', tokenData);
        }
      } catch (error) {
        console.error('Failed to track EMAIL survey open:', error);
        // Don't throw error as this shouldn't break the survey experience
      }
    };

    // Use setTimeout to ensure this runs after the component has fully rendered
    const timeoutId = setTimeout(trackEmailSurveyOpen, 100);

    return () => clearTimeout(timeoutId);
  }, [token, hasTrackedEmailOpen]);

  const steps = useMemo(() => adaptSurveyToSurveyPreviewSteps(survey), [survey]);
  const settings = survey?.settings || {};
  const title = survey?.title || "";
  const description = survey?.description || "";


  if (loading) return <div style={{ padding: 24 }}>Loading assessment…</div>;
  if (error) return <div style={{ padding: 24, color: "crimson" }}>{error}</div>;
  if (!survey) return <div style={{ padding: 24 }}>No assessment found.</div>;

  // Check if survey is locked (release field is true)
  if (survey.release) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
          padding: 3,
        }}
      >
        <Card
          sx={{
            maxWidth: 500,
            width: "100%",
            textAlign: "center",
            boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
            borderRadius: "16px",
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h4"
              sx={{
                mb: 2,
                color: "warning.main",
                fontWeight: "bold",
              }}
            >
              Assessment Unavailable
            </Typography>
            <Typography
              variant="body1"
              sx={{
                mb: 3,
                color: "text.secondary",
                lineHeight: 1.6,
              }}
            >
              This assessment has been temporarily stopped by the owner and is not currently accepting responses.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: "text.secondary",
                fontStyle: "italic",
              }}
            >
              Please check back later or contact the assessment owner for more information.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    );
  }

  // A voice-mode survey asks the same questions, read aloud and answered by voice.
  if (survey.respond_mode === "voice") {
    return <VoiceSurveyRunner survey={survey} steps={steps} surveyId={surveyId} token={token} />;
  }

  return (
    <SurveyPreview
      mode="respond"                 // ⬅ enables inputs + real submit
      steps={steps}
      settings={settings}
      title={title}
      description={description}
      surveyId={surveyId}
      token={token}
      onSubmit={async (answers, result) => {
        try {
          // Transform answers object into the format expected by the API
          const responses = Object.entries(answers).map(([questionId, answer]) => ({
            question_id: questionId,
            answer: Array.isArray(answer) ? answer.join(',') : answer
          }));

          // Submit survey responses to backend
          await api.submitSurveyResponse(surveyId, token, {
            responses: responses,
            completedAt: new Date().toISOString()
          });

          // optional redirect after a couple seconds
          if (settings?.redirectLink) {
            setTimeout(() => (window.location.href = settings.redirectLink), 1500);
          }
        } catch (error) {
          console.error("Failed to submit survey:", error);
          throw error; // Re-throw so SurveyPreview can handle the error
        }
      }}
    />
  );
}

// The respondent sees the survey in the language the link was issued in.
export default function RespondSurveyPage() {
  return (
    <RespondentIntlProvider>
      <RespondSurveyView />
    </RespondentIntlProvider>
  );
}
